import {
  useModalContext,
  ADDRESS_MODAL,
  TERMS_OF_USE_MODAL,
  LOGIN_REGISTRATION_MODAL,
} from 'contexts/modal';
import { useUserContext } from 'contexts/user';
import { GTM_CLICK_HOMEPAGE_SOLD_SIGN_IN } from 'constants/events';
import { AnalyticsController } from 'analytics/analytics-controller';
import { useLocale } from 'hooks/use-locale';
import { useTenantId } from 'hooks/use-tenant-id';
import { getLocalizedUrl } from 'utils/locale/get-localized-url';
import { TrackMapListingCardClicked } from 'components/search-es/tracking';

interface OnListingCardClickOptions {
  isHomePage?: boolean;
  isMapSidePanel?: boolean;
}

export type OnListingCardClickProps = {
  event?: unknown;
  isNoModalLocation?: boolean;
  addressPathWithFallback: string;
  listingId?: number;
  showsListing?: boolean;
  isPrivate: boolean;
  isSignInAndTermsOfUseRequired: boolean;
  isTermsRequired?: boolean;
  providerId?: number;
};

export function useOnListingCardClick({ isHomePage = false, isMapSidePanel = false }: OnListingCardClickOptions = {}) {
  const { openModal } = useModalContext();
  const { isAuthenticated } = useUserContext();
  const tenantId = useTenantId();
  const { currentLocale } = useLocale(tenantId);
  const openAddressPathWithFallback = (addressPathWithFallback: string) => {
    // append locale prefix
    const localizedUrl = getLocalizedUrl({
      locale: currentLocale,
      tenantId,
      path: addressPathWithFallback,
    });
    window.open(localizedUrl, '_blank');
  };

  const openProviderModal = (providerId: number) => {
    openModal(TERMS_OF_USE_MODAL, { providerId });
  };

  const openAuthModal = () => {
    openModal(LOGIN_REGISTRATION_MODAL);
  };

  const openListingModal = (addressPathWithFallback: string) => {
    openModal(ADDRESS_MODAL, { addressPath: addressPathWithFallback });
  };

  if (isHomePage) {
    const homePageClickFunction = ({ addressPathWithFallback, isPrivate, isSignInAndTermsOfUseRequired, isTermsRequired, providerId }: OnListingCardClickProps) => {
      if ((isSignInAndTermsOfUseRequired || isPrivate) && !isAuthenticated) {
        openAuthModal();
        AnalyticsController.trackEvent('Sold_Prices_Overlay');
        AnalyticsController.trackEvent(GTM_CLICK_HOMEPAGE_SOLD_SIGN_IN);
        if (isTermsRequired && providerId) {
          openProviderModal(providerId);
        }
      }
      else if (isTermsRequired && providerId) {
        openProviderModal(providerId);
      }
      else {
        openAddressPathWithFallback(addressPathWithFallback);
      }
    };
    return homePageClickFunction;
  }

  else {
    const listingCardClickFunction = ({ event, isNoModalLocation, addressPathWithFallback, showsListing, isPrivate, isSignInAndTermsOfUseRequired }: OnListingCardClickProps) => {
      if (isMapSidePanel) {
        TrackMapListingCardClicked();
      }
      if (isPrivate || ((isSignInAndTermsOfUseRequired || !showsListing) && !isAuthenticated)) {
        openListingModal(addressPathWithFallback);
      }
      else if (isNoModalLocation) {
        openAddressPathWithFallback(addressPathWithFallback);
      }
      else if (((event as MouseEvent).target as Element).tagName !== 'A') {
        openListingModal(addressPathWithFallback);
      }
    };
    return listingCardClickFunction;
  }
}